import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { rateCardsApi, type RateCard } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { formatManilaDate } from '@dispatchdash/shared';
import { errorText } from '@/components/FlashMessage';
import { useToast } from '@/components/Feedback';
import { formatDate } from '@/lib/format';

export function AdminRateCards() {
  const navigate = useNavigate();
  const { data: cards, loading, error: loadError } = useAsync(() => rateCardsApi.list(), []);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', effectiveFrom: formatManilaDate(new Date()) });
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleAdd = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const card = await rateCardsApi.create({
        name: form.name,
        // Start of the chosen day in Manila time.
        effectiveFrom: form.effectiveFrom ? new Date(`${form.effectiveFrom}T00:00:00+08:00`).toISOString() : undefined,
      });
      setShowAdd(false);
      toast(`Draft rate card “${form.name}” created — add lane rates next`);
      navigate(`/app/admin/rate-cards/${card.id}`);
    } catch (err) {
      setError(errorText(err, 'Could not create the rate card.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'name', header: 'Name' },
    {
      key: 'status',
      header: 'Status',
      value: (row: RateCard) => row.status,
      render: (row: RateCard) => <StatusBadge status={row.status} size="sm" />,
    },
    {
      key: 'effectiveFrom',
      header: 'Effective from',
      value: (row: RateCard) => row.effectiveFrom ?? '',
      render: (row: RateCard) =>
        row.effectiveFrom ? formatDate(row.effectiveFrom) : '—',
    },
    {
      key: 'version',
      header: 'Version',
      value: (row: RateCard) => row.version ?? 1,
      render: (row: RateCard) => row.version ?? 1,
    },
  ];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <p className="kicker">Rating</p>
            <h1>Rate cards</h1>
            <p className="text-muted">What you charge clients for each zone-to-zone trip, by vehicle type</p>
          </div>
          <Button onClick={() => setShowAdd(true)}>Add rate card</Button>
        </div>

        <DataTable
          columns={columns}
          data={cards ?? []}
          keyField="id"
          loading={loading}
          loadError={loadError}
          dense
          searchPlaceholder="Filter rate cards…"
          onRowClick={(row) => navigate(`/app/admin/rate-cards/${row.id}`)}
          emptyMessage="No rate cards yet. Click “Add rate card” to create a draft, add lane rates, then activate it."
        />

        <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add rate card">
          <form onSubmit={handleAdd}>
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="2026 Standard"
            />
            <Input
              label="Effective from"
              type="date"
              value={form.effectiveFrom}
              onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
            />
            {error && <p className="text-urgent">{error}</p>}
            <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: '1rem' }}>
              Cards start as draft. Add lane rates on the next screen, then activate.
            </p>
            <Button type="submit" loading={saving}>Create &amp; edit rates</Button>
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
